import * as React from 'react'
import { differenceInCalendarDays } from 'date-fns'

import { Skeleton } from '@/components/ui/skeleton'
import type { ClientActivity } from '@/features/patients/hooks/useClientActivity'
import type { ClientHistory } from '@/features/patients/hooks/useClientHistory'
import {
  dutyCounts,
  lastContact,
} from '@/features/patients/utils/clientActivity'
import { formatShortDate } from '@/features/patients/utils/clientDisplay'
import {
  appointmentDate,
  lastVisitDate,
  nextAppointment,
} from '@/features/patients/utils/clientHistory'
import { cn } from '@/lib/utils'

interface TileProps {
  label: string
  value: React.ReactNode
  detail: React.ReactNode
  isLoading: boolean
  /** Draws the value in the warning color: something is waiting on someone. */
  attention?: boolean
}

function Tile({ label, value, detail, isLoading, attention }: TileProps) {
  const labelId = React.useId()
  return (
    <div
      role="group"
      aria-labelledby={labelId}
      className="bg-app border-neutral-ui-border flex min-w-0 flex-col gap-0.5 rounded-lg border px-3 py-2.5 shadow-[var(--shadow-sm)]">
      <span id={labelId} className="text-muted-foreground text-xs font-medium">
        {label}
      </span>
      {isLoading ? (
        <>
          <Skeleton className="my-1 h-6 w-16" />
          <Skeleton className="h-3.5 w-20" />
        </>
      ) : (
        <>
          <span
            className={cn(
              'truncate text-xl font-semibold tracking-tight tabular-nums',
              attention ? 'text-warning-low-contrast' : 'text-text-heading'
            )}>
            {value}
          </span>
          <span className="text-muted-foreground truncate text-xs">
            {detail}
          </span>
        </>
      )}
    </div>
  )
}

/** "Today", "1d", "12d" — how long since, at a glance. */
function daysAgo(date: Date, now: Date): string {
  const days = differenceInCalendarDays(now, date)
  return days <= 0 ? 'Today' : `${days}d`
}

/**
 * At-a-glance status: how often the client is seen, when they are next due,
 * what outreach is still running and when someone last reached them. A tile is
 * left out when the viewer may not see its data.
 */
export function ClientSummaryTiles({
  history,
  activity,
  now,
}: {
  history: ClientHistory
  activity: ClientActivity
  now: Date
}) {
  const { visits, appointments } = history
  const { calls, duties } = activity
  const tiles: React.ReactNode[] = []

  if (visits.allowed) {
    const last = lastVisitDate(visits.rows)
    tiles.push(
      <Tile
        key="visits"
        label="Visits"
        isLoading={visits.isLoading}
        value={
          visits.isError
            ? '—'
            : `${visits.rows.length}${visits.truncated ? '+' : ''}`
        }
        detail={
          visits.isError
            ? 'Could not load visits'
            : last
              ? `last ${formatShortDate(last, now)}`
              : 'No visits on file'
        }
      />
    )
  }

  if (appointments.allowed) {
    const next = nextAppointment(appointments.rows, now)
    const date = next ? appointmentDate(next) : null
    tiles.push(
      <Tile
        key="next-appointment"
        label="Next appt"
        isLoading={appointments.isLoading}
        value={
          appointments.isError
            ? '—'
            : date
              ? formatShortDate(date, now)
              : 'None'
        }
        detail={
          appointments.isError
            ? 'Could not load appointments'
            : next
              ? (next.appointment_type ??
                next.appointment_reason ??
                'Appointment')
              : 'Nothing scheduled'
        }
      />
    )
  }

  const counts = dutyCounts(duties.rows)
  tiles.push(
    <Tile
      key="open-duties"
      label="Open duties"
      isLoading={duties.isLoading}
      value={duties.isError ? '—' : counts.open}
      attention={!duties.isError && counts.open > 0}
      detail={duties.isError ? 'Could not load duties' : `${counts.done} done`}
    />
  )

  if (calls.allowed) {
    const contact = lastContact(calls.rows)
    const date = contact ? new Date(contact.created_at) : null
    // With older calls unloaded, "none" only covers the calls in hand.
    const noneDetail = calls.hasMore
      ? `None in last ${calls.rows.length} call${calls.rows.length === 1 ? '' : 's'}`
      : 'No answered calls'
    tiles.push(
      <Tile
        key="last-contact"
        label="Last contact"
        isLoading={calls.isLoading}
        value={
          calls.isError
            ? '—'
            : date
              ? daysAgo(date, now)
              : calls.hasMore
                ? '—'
                : 'None'
        }
        detail={
          calls.isError
            ? 'Could not load calls'
            : date
              ? `${formatShortDate(date, now)} · ${contact?.type ? `${contact.type} ` : ''}call`
              : noneDetail
        }
      />
    )
  }

  // One row on a wide sheet, whatever subset of tiles the viewer can see.
  return (
    <div
      className={cn(
        'grid grid-cols-2 gap-2',
        tiles.length === 3 && 'sm:grid-cols-3',
        tiles.length === 4 && 'sm:grid-cols-4'
      )}>
      {tiles}
    </div>
  )
}
